import type { AnamneseFormData } from '../types/registration';
import { parseHeightMeters, parseWeightKg } from './masks';

const requireText = (value: string, message: string): string | undefined => (
  value.trim() ? undefined : message
);

const requireBoolean = (value: boolean | null, message: string): string | undefined => (
  value === null ? message : undefined
);

/** Mesmas regras de conteúdo da anamnese pública (sem e-mail/CPF/telefone/endereço). */
export type AnamneseContentFields = Pick<
  AnamneseFormData,
  | 'heightCm'
  | 'weightKg'
  | 'profession'
  | 'instagram'
  | 'previousOnlineConsultancy'
  | 'onlineCourse'
  | 'otherCourse'
  | 'courseFeedback'
  | 'trainingLevel'
  | 'trainingLevelOther'
  | 'trainingLocation'
  | 'fullGymName'
  | 'availableEquipment'
  | 'weeklyAvailability'
  | 'availableTrainingTime'
  | 'hadInjury'
  | 'injuryDescription'
  | 'hasPain'
  | 'painLocation'
  | 'painLimitsTraining'
  | 'limitedExercises'
  | 'currentlyExercises'
  | 'exerciseModalities'
  | 'timeWithoutExercise'
  | 'exerciseHistory'
  | 'enjoysTraining'
  | 'usedToEffort'
  | 'hasNutritionalSupport'
  | 'dietDetail'
  | 'mainGoal'
  | 'goalDetails'
  | 'takesMedication'
  | 'medications'
  | 'hadSurgery'
  | 'surgeries'
  | 'hasChronicDisease'
  | 'chronicDiseases'
  | 'hasFamilyHistory'
  | 'familyHistoryDescription'
  | 'hasChildren'
  | 'timeSinceDelivery'
  | 'abdominalDiastasis'
  | 'diastasisTreatment'
  | 'additionalInfo'
  | 'referralSource'
  | 'motivation'
>;

export const validateAnamneseContentFields = (
  data: AnamneseContentFields,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  const setError = (field: string, message?: string) => {
    if (message) {
      errors[field] = message;
    }
  };

  setError('heightCm', (() => {
    const heightValue = data.heightCm.trim();
    if (!heightValue) {
      return 'Informe a estatura em metros.';
    }

    const meters = parseHeightMeters(heightValue);
    if (meters == null) {
      return 'Informe a estatura no formato 1,75.';
    }

    if (meters < 0.5 || meters > 2.5) {
      return 'Informe uma estatura válida entre 0,50 e 2,50 m.';
    }

    return undefined;
  })());
  setError('weightKg', (() => {
    const weightValue = data.weightKg.trim();
    if (!weightValue) {
      return 'Informe o peso corporal.';
    }

    const kg = parseWeightKg(weightValue);
    if (kg == null) {
      return 'Informe o peso no formato 60,45.';
    }

    return undefined;
  })());

  setError(
    'previousOnlineConsultancy',
    requireText(data.previousOnlineConsultancy, 'Conte se já fez consultoria online e, se sim, por que interrompeu.'),
  );
  setError('onlineCourse', requireText(data.onlineCourse, 'Selecione uma opção de curso.'));
  if (data.onlineCourse === 'other') {
    setError('otherCourse', requireText(data.otherCourse, 'Informe qual curso.'));
  }
  setError(
    'courseFeedback',
    requireText(data.courseFeedback, 'Compartilhe o feedback sobre os cursos e sua fase atual.'),
  );

  setError('trainingLevel', requireText(data.trainingLevel, 'Selecione o nível de treinamento.'));
  if (data.trainingLevel === 'other') {
    setError('trainingLevelOther', requireText(data.trainingLevelOther, 'Descreva o nível de treinamento.'));
  }
  setError('trainingLocation', requireText(data.trainingLocation, 'Selecione o local de treino.'));
  if (data.trainingLocation === 'full_gym') {
    setError(
      'fullGymName',
      requireText(data.fullGymName, 'Descreva a academia e os equipamentos disponíveis.'),
    );
  }
  if (data.trainingLocation === 'home_condo_gym') {
    setError(
      'availableEquipment',
      requireText(data.availableEquipment, 'Descreva os materiais disponíveis para treino em casa.'),
    );
  }
  setError('weeklyAvailability', requireText(data.weeklyAvailability, 'Informe a frequência semanal.'));
  setError('availableTrainingTime', requireText(data.availableTrainingTime, 'Selecione o tempo disponível para cada treino.'));

  setError('hadInjury', requireBoolean(data.hadInjury, 'Informe se já sofreu acidente ou lesão osteomuscular.'));
  if (data.hadInjury === true) {
    setError('injuryDescription', requireText(data.injuryDescription, 'Descreva a lesão.'));
  }

  setError('hasPain', requireBoolean(data.hasPain, 'Informe se sente dores atualmente.'));
  if (data.hasPain === true) {
    setError('painLocation', requireText(data.painLocation, 'Informe o local da dor.'));
    setError('painLimitsTraining', requireBoolean(data.painLimitsTraining, 'Informe se a dor limita o treino.'));
    if (data.painLimitsTraining === true) {
      setError('limitedExercises', requireText(data.limitedExercises, 'Informe os exercícios que não consegue realizar.'));
    }
  }

  setError('currentlyExercises', requireBoolean(data.currentlyExercises, 'Informe se pratica exercícios atualmente.'));
  if (data.currentlyExercises === true) {
    setError('exerciseModalities', requireText(data.exerciseModalities, 'Informe quais exercícios pratica.'));
  }
  if (data.currentlyExercises === false) {
    setError('timeWithoutExercise', requireText(data.timeWithoutExercise, 'Informe há quanto tempo está sem treinar.'));
  }
  setError('exerciseHistory', requireText(data.exerciseHistory, 'Descreva seu histórico de exercícios.'));
  setError('enjoysTraining', requireBoolean(data.enjoysTraining, 'Informe se gosta de praticar exercícios.'));
  setError('usedToEffort', requireBoolean(data.usedToEffort, 'Informe se está acostumada com alto esforço.'));

  setError('hasNutritionalSupport', requireBoolean(data.hasNutritionalSupport, 'Informe se faz acompanhamento nutricional.'));
  setError(
    'dietDetail',
    requireText(data.dietDetail, 'Descreva alimentação, sono e saúde mental.'),
  );

  setError('mainGoal', requireText(data.mainGoal, 'Selecione o objetivo principal.'));
  setError('goalDetails', requireText(data.goalDetails, 'Descreva seus objetivos com mais detalhes.'));

  setError('takesMedication', requireBoolean(data.takesMedication, 'Informe se faz uso de medicamento contínuo.'));
  if (data.takesMedication === true) {
    setError('medications', requireText(data.medications, 'Informe qual medicamento.'));
  }

  setError('hadSurgery', requireBoolean(data.hadSurgery, 'Informe se já realizou cirurgia.'));
  if (data.hadSurgery === true) {
    setError('surgeries', requireText(data.surgeries, 'Informe qual cirurgia.'));
  }

  setError('hasChronicDisease', requireBoolean(data.hasChronicDisease, 'Informe se possui doença crônica.'));
  if (data.hasChronicDisease === true) {
    setError('chronicDiseases', requireText(data.chronicDiseases, 'Informe qual doença.'));
  }

  setError(
    'hasFamilyHistory',
    requireBoolean(data.hasFamilyHistory, 'Informe se possui histórico familiar de diabetes, hipertensão ou cardiopatia.'),
  );
  if (data.hasFamilyHistory === true) {
    setError(
      'familyHistoryDescription',
      requireText(data.familyHistoryDescription, 'Detalhe o histórico familiar.'),
    );
  }

  setError('hasChildren', requireBoolean(data.hasChildren, 'Informe se tem filhos.'));
  if (data.hasChildren === true) {
    setError('timeSinceDelivery', requireText(data.timeSinceDelivery, 'Informe há quanto tempo foi o parto.'));
    setError('abdominalDiastasis', requireBoolean(data.abdominalDiastasis, 'Informe se ficou com diástase após o parto.'));
    if (data.abdominalDiastasis === true) {
      setError('diastasisTreatment', requireText(data.diastasisTreatment, 'Informe se tratou a diástase.'));
    }
  }

  setError(
    'additionalInfo',
    requireText(data.additionalInfo, 'Informe se há alguma informação adicional ou escreva "não".'),
  );
  setError('referralSource', requireText(data.referralSource, 'Selecione como chegou até a consultoria.'));
  setError('motivation', requireText(data.motivation, 'Conte como chegou e o que motivou sua escolha.'));

  return errors;
};
